import React, { useState, useEffect } from "react";
import { formatCurrency } from "../../../utils/formatters";

const FINANCIAL_GOALS = {
  retirement: {
    label: "Retirement & Longevity",
    options: [
      "Retirement Income",
      "Early Retirement",
      "Semi-Retirement / Phased Withdrawal",
      "Delaying Social Security",
      "Longevity Planning / Outliving Assets",
    ],
  },
  education: {
    label: "🎓 Education & Dependents",
    options: [
      "Education for Child(ren)",
      "Education for Grandchildren",
      "Private School Funding (K–12)",
      "Special Needs or Dependent Care",
    ],
  },
  growth: {
    label: "📈 Growth & Wealth Building",
    options: [
      "Long-Term Wealth Accumulation",
      "Aggressive Capital Appreciation",
      "Building a Generational Legacy",
      "Investing Windfall / Inheritance",
    ],
  },
  security: {
    label: "🛡️ Security & Protection",
    options: [
      "Principal Preservation",
      "Emergency Fund Building",
      "Loss Mitigation / Market Downside Protection",
      "Inflation Protection",
    ],
  },
  realestate: {
    label: "🏡 Real Estate & Property",
    options: [
      "Buying a Primary Residence",
      "Buying a Second Home / Vacation Property",
      "Real Estate Investment (Rental Property)",
    ],
  },
  business: {
    label: "💼 Business & Professional",
    options: [
      "Start / Expand a Business",
      "Fund Sabbatical / Career Break",
      "Liquidity for Career Transition",
    ],
  },
  lifestyle: {
    label: "🎯 Lifestyle & Milestones",
    options: [
      "Big Purchase (e.g., car, boat, renovation)",
      "World Travel / Sabbatical",
      "Wedding / Major Life Event",
      "Charitable Giving / Philanthropy",
    ],
  },
  debt: {
    label: "⚖️ Debt & Obligations",
    options: [
      "Paying Off Student Loans",
      "Eliminating Consumer Debt",
      "Paying Down Mortgage",
    ],
  },
  estate: {
    label: "🧾 Estate & Legacy",
    options: [
      "Leave an Inheritance",
      "Maximize Tax-Efficient Wealth Transfer",
      "Establish a Trust / Donor-Advised Fund",
    ],
  },
  other: {
    label: "🧩 Custom / Other",
    options: [
      "General Investing",
      "Undecided / Exploring Options",
      "Custom Goal",
    ],
  },
};

const TIME_HORIZONS = [
  "0-3 months",
  "3-6 months",
  "6-months to a year",
  "1-3 years",
  "3-5 years",
  "5-10 years",
  "10-15 years",
  "15-20 years",
  "20-25 years",
  "25-30 years",
  "30+ years",
];

const RiskQuestionnaireWizard = ({
  isOpen,
  onClose,
  onComplete,
  initialData = {},
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    totalInvestableAssets: initialData.totalInvestableAssets || "",
    devastatingLossAmount: initialData.devastatingLossAmount || "",
    isHouseholdResponse: initialData.isHouseholdResponse || false,
    primaryFinancialGoal: initialData.primaryFinancialGoal || "",
    customGoal: initialData.customGoal || "",
    timeHorizon: initialData.timeHorizon || "",
    comfortZoneChoice: initialData.comfortZoneChoice || "",
    lossReaction: initialData.lossReaction || "",
    upsideTradeoff: initialData.upsideTradeoff || "",
    volatilityComfort: initialData.volatilityComfort || "",
    additionalRisk: initialData.additionalRisk || "",
    currentScenario: null,
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  // Helper to check if a field has been touched and has an error
  const showError = (field) => touched[field] && errors[field];

  // Validation for Step 1
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.totalInvestableAssets)
          newErrors.totalInvestableAssets = "Required";
        if (!formData.devastatingLossAmount)
          newErrors.devastatingLossAmount = "Required";
        if (!formData.primaryFinancialGoal)
          newErrors.primaryFinancialGoal = "Required";
        if (
          formData.primaryFinancialGoal === "Custom Goal" &&
          !formData.customGoal
        ) {
          newErrors.customGoal = "Required";
        }
        if (!formData.timeHorizon) newErrors.timeHorizon = "Required";
        break;
      case 2:
        if (!formData.comfortZoneChoice)
          newErrors.comfortZoneChoice = "Required";
        break;
      case 3:
        if (!formData.lossReaction) newErrors.lossReaction = "Required";
        break;
      case 4:
        if (!formData.upsideTradeoff) newErrors.upsideTradeoff = "Required";
        break;
      case 5:
        if (!formData.volatilityComfort)
          newErrors.volatilityComfort = "Required";
        break;
      case 6:
        if (!formData.additionalRisk) newErrors.additionalRisk = "Required";
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  // Handle numeric input with formatting
  const handleNumericInput = (e) => {
    const { name, value } = e.target;
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, "");
    setFormData((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  // Initialize first scenario
  const initializeScenario = () => {
    // Choose starting loss based on time horizon
    const shortTermHorizons = [
      "0-3 months",
      "3-6 months",
      "6-months to a year",
    ];
    const startingLoss = shortTermHorizons.includes(formData.timeHorizon)
      ? 5
      : 10;

    return {
      scenarioLoss: startingLoss,
      scenarioGain: startingLoss * 2,
    };
  };

  // Handle next step
  const handleNext = () => {
    if (!validateStep(currentStep)) return;

    if (currentStep === 1) {
      const scenario = initializeScenario();
      setFormData((prev) => ({
        ...prev,
        currentScenario: scenario,
      }));
    } else if (currentStep === 7) {
      // Calculate final risk score
      const riskScore = calculateRiskScore(formData);
      onComplete({
        ...formData,
        riskScore,
      });
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  // Handle back
  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // Render progress indicator
  const renderProgress = () => {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Step {currentStep} of {8}
          </span>
          <div className="flex space-x-1">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  i + 1 === currentStep
                    ? "bg-blue-600"
                    : i + 1 < currentStep
                      ? "bg-blue-200"
                      : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render tooltip/info icon
  const renderInfoIcon = (text) => (
    <div className="group relative inline-block ml-1">
      <svg
        className="w-4 h-4 text-gray-400"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
      <div className="invisible group-hover:visible absolute z-10 w-64 p-2 mt-2 text-sm text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {text}
      </div>
    </div>
  );

  // Render Step 1: Basic Info
  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Total Investable Assets
        </label>
        <div className="mt-1 relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-500 sm:text-sm">$</span>
          </div>
          <input
            type="text"
            name="totalInvestableAssets"
            value={formData.totalInvestableAssets}
            onChange={handleNumericInput}
            className={`block w-full pl-7 pr-12 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              showError("totalInvestableAssets")
                ? "border-red-300 text-red-900 dark:text-red-500 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            }`}
            placeholder="0.00"
          />
        </div>
        {showError("totalInvestableAssets") && (
          <p className="mt-2 text-sm text-red-600">
            {errors.totalInvestableAssets}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Devastating Loss Amount
          {renderInfoIcon(
            "The amount of loss that would significantly impact your financial well-being or lifestyle",
          )}
        </label>
        <div className="mt-1 relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-500 sm:text-sm">$</span>
          </div>
          <input
            type="text"
            name="devastatingLossAmount"
            value={formData.devastatingLossAmount}
            onChange={handleNumericInput}
            className={`block w-full pl-7 pr-12 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              showError("devastatingLossAmount")
                ? "border-red-300 text-red-900 dark:text-red-500 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            }`}
            placeholder="0.00"
          />
        </div>
        {showError("devastatingLossAmount") && (
          <p className="mt-2 text-sm text-red-600">
            {errors.devastatingLossAmount}
          </p>
        )}
      </div>

      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            name="isHouseholdResponse"
            checked={formData.isHouseholdResponse}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Household Response?
          </span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Primary Financial Goal
        </label>
        <select
          name="primaryFinancialGoal"
          value={formData.primaryFinancialGoal}
          onChange={handleInputChange}
          className={`mt-1 block w-full pl-3 pr-10 py-2 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md ${
            showError("primaryFinancialGoal")
              ? "border-red-300 text-red-900 dark:text-red-500"
              : ""
          }`}
        >
          <option value="">Select a goal</option>
          {Object.entries(FINANCIAL_GOALS).map(
            ([category, { label, options }]) => (
              <optgroup key={category} label={label}>
                {options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </optgroup>
            ),
          )}
        </select>
        {showError("primaryFinancialGoal") && (
          <p className="mt-2 text-sm text-red-600">
            {errors.primaryFinancialGoal}
          </p>
        )}
      </div>

      {formData.primaryFinancialGoal === "Custom Goal" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Specify Custom Goal
          </label>
          <input
            type="text"
            name="customGoal"
            value={formData.customGoal}
            onChange={handleInputChange}
            className={`mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              showError("customGoal") ? "border-red-300" : ""
            }`}
          />
          {showError("customGoal") && (
            <p className="mt-2 text-sm text-red-600">{errors.customGoal}</p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Time Horizon
          {renderInfoIcon(
            "The expected length of time you plan to remain invested to achieve your goal",
          )}
        </label>
        <select
          name="timeHorizon"
          value={formData.timeHorizon}
          onChange={handleInputChange}
          className={`mt-1 block w-full pl-3 pr-10 py-2 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md ${
            showError("timeHorizon") ? "border-red-300 text-red-900 dark:text-red-500" : ""
          }`}
        >
          <option value="">Select time horizon</option>
          {TIME_HORIZONS.map((horizon) => (
            <option key={horizon} value={horizon}>
              {horizon}
            </option>
          ))}
        </select>
        {showError("timeHorizon") && (
          <p className="mt-2 text-sm text-red-600">{errors.timeHorizon}</p>
        )}
      </div>
    </div>
  );

  const calculateRiskScore = (data) => {
    let score = 50; // Base score

    // Adjust based on comfort zone choice
    if (data.comfortZoneChoice === "Portfolio A") score -= 10;
    if (data.comfortZoneChoice === "Portfolio B") score += 10;

    // Adjust based on loss reaction
    if (data.lossReaction === "Sell Everything") score -= 15;
    if (data.lossReaction === "Sell Some") score -= 5;
    if (data.lossReaction === "Buy More") score += 15;

    // Adjust based on upside tradeoff
    if (data.upsideTradeoff === "Conservative") score -= 10;
    if (data.upsideTradeoff === "Aggressive") score += 10;

    // Adjust based on time horizon
    const horizonYears = parseInt(data.timeHorizon);
    if (horizonYears <= 2) score -= 10;
    else if (horizonYears >= 10) score += 10;

    // Adjust based on volatility comfort
    if (data.volatilityComfort === "Very Uncomfortable") score -= 15;
    if (data.volatilityComfort === "Very Comfortable") score += 15;

    // Adjust based on additional risk
    if (data.additionalRisk === "Less") score -= 10;
    if (data.additionalRisk === "More") score += 10;

    // Ensure score stays within 1-99 range
    return Math.min(Math.max(score, 1), 99);
  };

  const renderComfortZone = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
        Portfolio Comparison
      </h4>
      <div className="grid grid-cols-2 gap-6">
        <div className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg bg-white dark:bg-gray-800">
          <h5 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Portfolio A</h5>
          <p className="text-gray-700 dark:text-gray-300">Lower risk, more stable returns</p>
          <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <li>• Expected return: 6-8%</li>
            <li>• Potential loss: -5% to -10%</li>
            <li>• More bonds, less stocks</li>
          </ul>
        </div>
        <div className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg bg-white dark:bg-gray-800">
          <h5 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Portfolio B</h5>
          <p className="text-gray-700 dark:text-gray-300">Higher risk, potentially higher returns</p>
          <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <li>• Expected return: 8-12%</li>
            <li>• Potential loss: -15% to -20%</li>
            <li>• More stocks, less bonds</li>
          </ul>
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Which portfolio aligns better with your comfort zone?
        </label>
        <div className="space-y-2">
          {["Portfolio A", "Portfolio B"].map((option) => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name="comfortZoneChoice"
                value={option}
                checked={formData.comfortZoneChoice === option}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2">{option}</span>
            </label>
          ))}
        </div>
        {showError("comfortZoneChoice") && (
          <p className="mt-2 text-sm text-red-600">
            {errors.comfortZoneChoice}
          </p>
        )}
      </div>
    </div>
  );

  const renderLossReaction = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
        Market Loss Scenario
      </h4>
      <p className="text-gray-700 dark:text-gray-300">
        If your portfolio dropped by {formData.currentScenario?.scenarioLoss}%
        in a short period, what would you most likely do?
      </p>
      <div className="space-y-2">
        {["Sell Everything", "Sell Some", "Do Nothing", "Buy More"].map(
          (option) => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name="lossReaction"
                value={option}
                checked={formData.lossReaction === option}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2">{option}</span>
            </label>
          ),
        )}
      </div>
      {showError("lossReaction") && (
        <p className="mt-2 text-sm text-red-600">{errors.lossReaction}</p>
      )}
    </div>
  );

  const renderUpsideTradeoff = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">Upside Potential</h4>
      <p className="text-gray-700 dark:text-gray-300">
        If you had to choose between a guaranteed{" "}
        {formData.currentScenario?.scenarioLoss}% gain or a 50/50 chance of
        either no gain or a {formData.currentScenario?.scenarioGain}% gain,
        which would you prefer?
      </p>
      <div className="space-y-2">
        {[
          "Conservative: Take the guaranteed gain",
          "Moderate: Need more information",
          "Aggressive: Take the chance for higher gain",
        ].map((option) => (
          <label key={option} className="flex items-center">
            <input
              type="radio"
              name="upsideTradeoff"
              value={option.split(":")[0]}
              checked={formData.upsideTradeoff === option.split(":")[0]}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2">{option}</span>
          </label>
        ))}
      </div>
      {showError("upsideTradeoff") && (
        <p className="mt-2 text-sm text-red-600">{errors.upsideTradeoff}</p>
      )}
    </div>
  );

  const renderVolatilityComfort = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">Volatility Comfort</h4>
      <p className="text-gray-700 dark:text-gray-300">
        How comfortable are you with short-term market fluctuations?
      </p>
      <div className="space-y-2">
        {[
          "Very Uncomfortable",
          "Somewhat Uncomfortable",
          "Neutral",
          "Somewhat Comfortable",
          "Very Comfortable",
        ].map((option) => (
          <label key={option} className="flex items-center">
            <input
              type="radio"
              name="volatilityComfort"
              value={option}
              checked={formData.volatilityComfort === option}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2">{option}</span>
          </label>
        ))}
      </div>
      {showError("volatilityComfort") && (
        <p className="mt-2 text-sm text-red-600">{errors.volatilityComfort}</p>
      )}
    </div>
  );

  const renderAdditionalRisk = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">Risk Adjustment</h4>
      <p className="text-gray-700 dark:text-gray-300">
        Based on your responses so far, would you prefer to take more or less
        risk than suggested?
      </p>
      <div className="space-y-2">
        {["Less", "Same", "More"].map((option) => (
          <label key={option} className="flex items-center">
            <input
              type="radio"
              name="additionalRisk"
              value={option}
              checked={formData.additionalRisk === option}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2">{option}</span>
          </label>
        ))}
      </div>
      {showError("additionalRisk") && (
        <p className="mt-2 text-sm text-red-600">{errors.additionalRisk}</p>
      )}
    </div>
  );

  const renderReview = () => {
    const riskScore = calculateRiskScore(formData);
    return (
      <div className="space-y-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Review Your Responses
        </h4>

        {/* Basic Info Summary */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Basic Information</h5>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Total Investable Assets
              </p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">${formData.totalInvestableAssets}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Devastating Loss Amount
              </p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">${formData.devastatingLossAmount}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Primary Financial Goal
              </p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{formData.primaryFinancialGoal}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Time Horizon</p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{formData.timeHorizon}</p>
            </div>
          </div>
        </div>

        {/* Risk Assessment Summary */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Risk Assessment</h5>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Comfort Zone</p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{formData.comfortZoneChoice}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Loss Reaction</p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{formData.lossReaction}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Upside Trade-off
              </p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{formData.upsideTradeoff}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Volatility Comfort
              </p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{formData.volatilityComfort}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Additional Risk Preference
              </p>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{formData.additionalRisk}</p>
            </div>
          </div>
        </div>

        {/* Risk Score Display */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
            Calculated Risk Score
          </h5>
          <div className="flex items-center space-x-4">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className="bg-blue-600 dark:bg-blue-500 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${riskScore}%` }}
              ></div>
            </div>
            <span className="text-lg font-medium text-gray-900 dark:text-gray-100">{riskScore}</span>
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            This score represents your risk tolerance on a scale of 1-99, where
            higher numbers indicate greater risk tolerance.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between mt-6">
          <div className="flex space-x-4">
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              Back
            </button>
            <button
              onClick={() => setCurrentStep(1)}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              Start Over
            </button>
          </div>
          <button
            onClick={() => {
              const finalData = {
                ...formData,
                riskScore,
              };
              onComplete(finalData);
            }}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200"
          >
            Confirm & Finish
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-5 border border-gray-200 dark:border-gray-700 w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-900">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Risk Tolerance Questionnaire
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 text-gray-500 dark:text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {renderProgress()}

        <div className="mt-6">
          {currentStep === 1 && renderBasicInfo()}
          {currentStep === 2 && renderComfortZone()}
          {currentStep === 3 && renderLossReaction()}
          {currentStep === 4 && renderUpsideTradeoff()}
          {currentStep === 5 && renderVolatilityComfort()}
          {currentStep === 6 && renderAdditionalRisk()}
          {currentStep === 7 && renderReview()}
        </div>

        {currentStep !== 7 && (
          <div className="mt-6 flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                Back
              </button>
            )}
            <div className="ml-auto">
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskQuestionnaireWizard;
